import { apiFetch } from "@/integrations/backend/client";
import type { Enums, Tables } from "../backend/types";

export type LegalDocumentRow = Tables<"legal_documents">;
export type LegalDocumentType = Enums<"legal_document_type">;

export async function getCurrentLegalDocumentByType(type: LegalDocumentType): Promise<LegalDocumentRow | null> {
  const res = await apiFetch(`/api/v1/legal/current?type=${encodeURIComponent(type)}`, { method: "GET" });
  if (!res.ok) {
    if (res.status === 404) return null;
    console.warn("[legal] getCurrentLegalDocumentByType error", res.status);
    return null;
  }
  const json = await res.json();
  if (json && json.document === null) return null;
  return json as LegalDocumentRow;
}

// Admin: list legal documents
export async function listLegalDocuments(): Promise<LegalDocumentRow[]> {
  const res = await apiFetch("/api/v1/admin/legal-documents", { method: "GET" });
  if (!res.ok) throw new Error(`legal_admin_list_failed:${res.status}`);
  return (await res.json()) as any;
}

// Admin: create a new version
export async function createLegalDocument(input: {
  type: LegalDocumentType;
  version: number;
  title: string;
  content: string;
  is_current: boolean;
}): Promise<string> {
  const res = await apiFetch("/api/v1/admin/legal-documents", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`legal_admin_create_failed:${res.status}`);
  const json = await res.json();
  return json.id as string;
}

// Admin: delete a document
export async function deleteLegalDocument(id: string): Promise<void> {
  const res = await apiFetch(`/api/v1/admin/legal-documents/${id}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`legal_admin_delete_failed:${res.status}`);
}

// Accept a legal document
export async function acceptLegalDocument(documentId: string): Promise<void> {
  const res = await apiFetch("/api/v1/legal/accept", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ document_id: documentId }),
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: "Unknown error" }));
    throw new Error(`legal_accept_failed:${res.status}:${error.detail || "Unknown error"}`);
  }
}

// Get legal status (current documents with acceptance info)
export interface LegalStatusItem {
  document: {
    id: string;
    type: "terms" | "privacy" | "liability_waiver";
    version: number;
    title: string;
    content: string;
    published_at: string | null;
    created_at: string | null;
  };
  accepted_at: string | null;
  is_accepted: boolean;
  most_recent_acceptance_at: string | null;
}

export async function getLegalStatus(): Promise<LegalStatusItem[]> {
  const res = await apiFetch("/api/v1/legal/status", { method: "GET" });
  if (!res.ok) throw new Error(`legal_status_failed:${res.status}`);
  return (await res.json()) as LegalStatusItem[];
}


